import { forwardRef, useState } from 'react'
import { cn } from '@/libs/utils'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface PhoneNumberInputProps {
  value?: { prefix: string; number: string }
  onChange?: (value: { prefix: string; number: string }) => void
  disabled?: boolean
  placeholder?: string
  className?: string
}

const COUNTRY_CODES = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+84', country: 'VN', flag: '🇻🇳' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+82', country: 'KR', flag: '🇰🇷' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+91', country: 'IN', flag: '🇮🇳' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+39', country: 'IT', flag: '🇮🇹' },
  { code: '+34', country: 'ES', flag: '🇪🇸' },
  { code: '+55', country: 'BR', flag: '🇧🇷' },
  { code: '+52', country: 'MX', flag: '🇲🇽' },
  { code: '+7', country: 'RU', flag: '🇷🇺' },
  { code: '+27', country: 'ZA', flag: '🇿🇦' },
  { code: '+971', country: 'AE', flag: '🇦🇪' },
  { code: '+65', country: 'SG', flag: '🇸🇬' },
  { code: '+60', country: 'MY', flag: '🇲🇾' },
  { code: '+66', country: 'TH', flag: '🇹🇭' },
  { code: '+62', country: 'ID', flag: '🇮🇩' },
  { code: '+63', country: 'PH', flag: '🇵🇭' },
]

export const PhoneNumberInput = forwardRef<
  HTMLDivElement,
  PhoneNumberInputProps
>(({ value, onChange, disabled, placeholder, className }, ref) => {
  const [prefix, setPrefix] = useState(value?.prefix || '+84')
  const [number, setNumber] = useState(value?.number || '')

  const handlePrefixChange = (newPrefix: string) => {
    setPrefix(newPrefix)
    onChange?.({ prefix: newPrefix, number })
  }

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = e.target.value
    setNumber(newNumber)
    onChange?.({ prefix, number: newNumber })
  }

  return (
    <div ref={ref} className={cn('flex gap-2', className)}>
      <Select
        value={prefix}
        onValueChange={handlePrefixChange}
        disabled={disabled}
      >
        <SelectTrigger className='w-[140px]'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className='max-h-[300px]'>
          {COUNTRY_CODES.map((country) => (
            <SelectItem key={country.code} value={country.code}>
              <span className='flex items-center gap-2'>
                <span>{country.flag}</span>
                <span>{country.code}</span>
                <span className='text-xs text-muted-foreground'>
                  {country.country}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type='tel'
        value={number}
        onChange={handleNumberChange}
        disabled={disabled}
        placeholder={placeholder || 'Enter phone number'}
        className='flex-1'
      />
    </div>
  )
})

PhoneNumberInput.displayName = 'PhoneNumberInput'
